import { Prisma, type FinancialAccount, type PrismaClient } from '@prisma/client';
import { Router, type Request } from 'express';
import { z } from 'zod';
import { audit } from '../../core/audit.js';
import type { Config } from '../../core/config.js';
import { encrypt } from '../../core/crypto.js';
import { HttpError } from '../../core/errors.js';
import { asyncHandler, clientIp, pageParams, parse } from '../../core/http.js';
import { idempotent } from '../../core/idempotency.js';
import { nextDocumentNumber } from '../../core/sequence.js';
import { accountBalances, postEntry } from '../accounting/posting.service.js';
import { isoDate, toDate } from '../accounting/periods.js';
import { authenticate, requirePermission } from '../auth/auth.middleware.js';
import { createFinancialAccount } from './banking.service.js';

interface Deps {
  db: PrismaClient;
  config: Config;
}

const D = (v: Prisma.Decimal.Value) => new Prisma.Decimal(v);
const dateStr = z.string().regex(/^\d{4}-\d{2}-\d{2}$/, 'Use YYYY-MM-DD');
const amount = z
  .union([z.string(), z.number()])
  .transform((v) => String(v))
  .refine((v) => /^\d{1,15}(\.\d{1,4})?$/.test(v), 'Enter a valid amount');

const KINDS = ['bank', 'mobile_money', 'cash', 'gateway'] as const;

export function createBankingRouter({ db, config }: Deps) {
  const router = Router();
  router.use('/banking', authenticate({ db, config }), idempotent(db));
  const actor = (req: Request) => ({
    companyId: req.auth!.companyId,
    userId: req.auth!.userId,
    ip: clientIp(req),
  });

  const view = (a: FinancialAccount, balance?: Prisma.Decimal) => ({
    id: a.id,
    kind: a.kind,
    name: a.name,
    bankName: a.bankName,
    accountName: a.accountName,
    // Only the last four digits ever leave the server (the number is encrypted at rest).
    accountNumberMasked: a.accountNumberLast4 ? `••••${a.accountNumberLast4}` : null,
    branch: a.branch,
    currency: a.currency,
    openingBalance: a.openingBalance.toFixed(4),
    openingDate: isoDate(a.openingDate),
    ledgerAccountId: a.ledgerAccountId,
    status: a.status,
    ...(balance ? { balance: balance.toFixed(4) } : {}),
  });

  async function balancesFor(companyId: number, accounts: FinancialAccount[]) {
    const bal = await accountBalances(db, companyId, {
      accountIds: accounts.map((a) => a.ledgerAccountId),
    });
    return new Map(
      accounts.map((a) => {
        const b = bal.find((x) => x.accountId === a.ledgerAccountId);
        return [a.id, b ? b.debit.minus(b.credit) : D(0)] as const;
      }),
    );
  }

  router.get(
    '/banking/accounts',
    requirePermission('banking.account.view', 'banking.transfer.view'),
    asyncHandler(async (req, res) => {
      const kind = typeof req.query.kind === 'string' ? req.query.kind : undefined;
      const group = typeof req.query.group === 'string' ? req.query.group : undefined; // bank | payment
      const accounts = await db.financialAccount.findMany({
        where: {
          companyId: req.auth!.companyId,
          ...(kind ? { kind } : {}),
          ...(group === 'bank'
            ? { kind: 'bank' }
            : group === 'payment'
              ? { kind: { not: 'bank' } }
              : {}),
        },
        orderBy: [{ kind: 'asc' }, { name: 'asc' }],
      });
      const balances = await balancesFor(req.auth!.companyId, accounts);
      const items = accounts.map((a) => view(a, balances.get(a.id)));
      const total = [...balances.values()].reduce((s, v) => s.plus(v), D(0));
      res.json({ items, totalBalance: total.toFixed(4) });
    }),
  );

  const createSchema = z.object({
    kind: z.enum(KINDS),
    name: z.string().trim().min(2).max(150),
    bankName: z.string().trim().max(150).optional(),
    accountName: z.string().trim().max(150).optional(),
    accountNumber: z.string().trim().min(4).max(40).optional(),
    branch: z.string().trim().max(150).optional(),
    currency: z
      .string()
      .length(3)
      .transform((v) => v.toUpperCase())
      .default('UGX'),
    openingBalance: z
      .union([z.string(), z.number()])
      .transform((v) => String(v))
      .refine((v) => /^-?\d{1,15}(\.\d{1,4})?$/.test(v), 'Enter a valid amount')
      .default('0'),
    openingDate: dateStr,
  });

  router.post(
    '/banking/accounts',
    requirePermission('banking.account.create'),
    asyncHandler(async (req, res) => {
      const body = parse(createSchema, req.body);
      const companyId = req.auth!.companyId;
      const created = await db.$transaction((tx) =>
        createFinancialAccount(tx, config.ENCRYPTION_KEY, { ...actor(req), userId: req.auth!.userId }, body),
      );
      const balance = (await balancesFor(companyId, [created])).get(created.id);
      res.status(201).json(view(created, balance));
    }),
  );

  router.patch(
    '/banking/accounts/:id',
    requirePermission('banking.account.edit'),
    asyncHandler(async (req, res) => {
      const id = Number(req.params.id);
      const body = parse(
        z.object({
          name: z.string().trim().min(2).max(150).optional(),
          bankName: z.string().trim().max(150).nullable().optional(),
          accountName: z.string().trim().max(150).nullable().optional(),
          accountNumber: z.string().trim().min(4).max(40).optional(),
          branch: z.string().trim().max(150).nullable().optional(),
          status: z.enum(['active', 'inactive']).optional(),
        }),
        req.body,
      );
      const before = await db.financialAccount.findFirst({
        where: { id, companyId: req.auth!.companyId },
      });
      if (!before) throw new HttpError(404, 'NOT_FOUND', 'Account not found');
      const balance = (await balancesFor(before.companyId, [before])).get(id)!;
      if (body.status === 'inactive' && !balance.isZero()) {
        throw new HttpError(
          409,
          'HAS_BALANCE',
          'Move the remaining balance out before deactivating this account',
        );
      }
      const { accountNumber, ...rest } = body;
      const after = await db.$transaction(async (tx) => {
        const updated = await tx.financialAccount.update({
          where: { id },
          data: {
            ...rest,
            ...(accountNumber
              ? {
                  accountNumberEnc: encrypt(accountNumber, config.ENCRYPTION_KEY),
                  accountNumberLast4: accountNumber.slice(-4),
                }
              : {}),
          },
        });
        if (body.name || body.status) {
          await tx.account.update({
            where: { id: before.ledgerAccountId },
            data: {
              ...(body.name ? { name: body.name } : {}),
              ...(body.status ? { isActive: body.status === 'active' } : {}),
            },
          });
        }
        await audit(tx, actor(req), {
          action: 'banking.account_updated',
          entity: 'financial_account',
          entityId: id,
          before: view(before),
          after: view(updated),
        });
        return updated;
      });
      res.json(view(after, balance));
    }),
  );

  // ───── transfers ─────
  const transferSchema = z.object({
    fromAccountId: z.number().int().positive(),
    toAccountId: z.number().int().positive(),
    amount,
    fee: amount.default('0'),
    date: dateStr,
    reference: z.string().trim().max(100).optional(),
    notes: z.string().trim().max(300).optional(),
  });

  router.get(
    '/banking/transfers',
    requirePermission('banking.transfer.view'),
    asyncHandler(async (req, res) => {
      const { page, pageSize, skip, take } = pageParams(req.query as Record<string, unknown>);
      const where = { companyId: req.auth!.companyId };
      const [total, rows, accounts] = await Promise.all([
        db.accountTransfer.count({ where }),
        db.accountTransfer.findMany({
          where,
          orderBy: [{ transferDate: 'desc' }, { id: 'desc' }],
          skip,
          take,
        }),
        db.financialAccount.findMany({ where, select: { id: true, name: true } }),
      ]);
      const names = new Map(accounts.map((a) => [a.id, a.name]));
      res.json({
        items: rows.map((t) => ({
          id: t.id,
          transferNo: t.transferNo,
          date: isoDate(t.transferDate),
          from: names.get(t.fromAccountId) ?? '',
          to: names.get(t.toAccountId) ?? '',
          fromAccountId: t.fromAccountId,
          toAccountId: t.toAccountId,
          amount: t.amount.toFixed(4),
          fee: t.fee.toFixed(4),
          reference: t.reference,
          journalEntryId: t.journalEntryId,
        })),
        total,
        page,
        pageSize,
      });
    }),
  );

  router.post(
    '/banking/transfers',
    requirePermission('banking.transfer.create'),
    asyncHandler(async (req, res) => {
      const body = parse(transferSchema, req.body);
      if (body.fromAccountId === body.toAccountId)
        throw new HttpError(400, 'SAME_ACCOUNT', 'Choose two different accounts');
      const amt = D(body.amount);
      const fee = D(body.fee);
      if (!amt.gt(0))
        throw new HttpError(400, 'INVALID_AMOUNT', 'Amount must be greater than zero');
      const companyId = req.auth!.companyId;

      const transfer = await db.$transaction(async (tx) => {
        const accounts = await tx.financialAccount.findMany({
          where: { companyId, id: { in: [body.fromAccountId, body.toAccountId] } },
        });
        const from = accounts.find((a) => a.id === body.fromAccountId);
        const to = accounts.find((a) => a.id === body.toAccountId);
        if (!from || !to) throw new HttpError(404, 'NOT_FOUND', 'Account not found');
        if (from.status !== 'active' || to.status !== 'active')
          throw new HttpError(409, 'INACTIVE', 'Both accounts must be active');

        const transferNo = await nextDocumentNumber(tx, companyId, 'transfer', toDate(body.date));
        const lines = [
          { accountId: to.ledgerAccountId, debit: amt.toFixed(4), memo: `From ${from.name}` },
          ...(fee.gt(0)
            ? [{ systemKey: 'BANK_CHARGES', debit: fee.toFixed(4), memo: 'Transfer fee' }]
            : []),
          {
            accountId: from.ledgerAccountId,
            credit: amt.plus(fee).toFixed(4),
            memo: `To ${to.name}`,
          },
        ];
        const created = await tx.accountTransfer.create({
          data: {
            companyId,
            transferNo,
            fromAccountId: from.id,
            toAccountId: to.id,
            amount: amt,
            fee,
            transferDate: toDate(body.date),
            reference: body.reference,
            notes: body.notes,
            createdBy: req.auth!.userId,
          },
        });
        // The source link is frozen once an entry is posted, so the transfer row must exist first.
        const entry = await postEntry(tx, {
          companyId,
          date: body.date,
          description: `Transfer ${transferNo}: ${from.name} to ${to.name}`,
          sourceType: 'transfer',
          sourceId: created.id,
          reference: body.reference,
          lines,
          userId: req.auth!.userId,
        });
        await tx.accountTransfer.update({
          where: { id: created.id },
          data: { journalEntryId: entry.id },
        });
        await audit(tx, actor(req), {
          action: 'banking.transfer_created',
          entity: 'account_transfer',
          entityId: created.id,
          after: {
            transferNo,
            from: from.name,
            to: to.name,
            amount: amt.toFixed(4),
            fee: fee.toFixed(4),
          },
        });
        return { ...created, journalEntryId: entry.id };
      });
      res.status(201).json({
        id: transfer.id,
        transferNo: transfer.transferNo,
        journalEntryId: transfer.journalEntryId,
      });
    }),
  );

  return router;
}
